import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import { handleAuthError } from "../../utils";
import {
    Col,
    Container,
    Form,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import withAuth from "../withAuth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../Network";
import ActionsDropdown from "../../components/Common/ActionsDropdown";
import ChildProgramsModal from "../../components/Common/ChildProgramsModal";
import { useConfirm } from "../../contexts/ConfirmContext";
import { isMarketerReadOnly } from "../../helpers/adminRole";

const TIMES_ = [
    "12:00AM", "1:00AM", "2:00AM", "3:00AM", "4:00AM", "5:00AM", "6:00AM", "7:00AM",
    "8:00AM", "9:00AM", "10:00AM", "11:00AM", "12:00PM", "1:00PM", "2:00PM", "3:00PM",
    "4:00PM", "5:00PM", "6:00PM", "7:00PM", "8:00PM", "9:00PM", "10:00PM", "11:00PM",
];
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ISSUE_LABELS = {
    level_repeat: "Repeated level",
    level_gap: "Skipped level",
    level_regression: "Moved backward",
    invalid_package_level: "Invalid package level",
    missing_package: "Missing package",
    age_out_of_range: "Age out of range",
    teacher_level_mismatch: "Teacher level mismatch",
};

const severityColor = (severity) =>
    severity === "high" ? "danger" : severity === "medium" ? "warning" : "secondary";

const ManageRepeated = () => {
    document.title = "Manage Repeated | RYD Admin";
    const { showConfirm } = useConfirm();
    const readOnly = isMarketerReadOnly();

    const [rows, setRows] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [assignableTeachers, setAssignableTeachers] = useState([]);
    const [cohorts, setCohorts] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [search, setSearch] = useState("");
    const [issueFilter, setIssueFilter] = useState("");
    const [cohortFilter, setCohortFilter] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    // edit modal
    const [editModal, setEditModal] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [programMData, setProgramMData] = useState({});

    // assign teacher modal
    const [assignModal, setAssignModal] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState("");

    // schedule conflict modal
    const [scheduleConflictModal, setScheduleConflictModal] = useState(false);
    const [scheduleConflicts, setScheduleConflicts] = useState([]);
    const [pendingTeacherPayload, setPendingTeacherPayload] = useState(null);

    // view all programs modal
    const [programsModal, setProgramsModal] = useState(false);
    const [programsChild, setProgramsChild] = useState(null);

    const openProgramsModal = (program) => {
        setProgramsChild(program?.child ? { ...program.child, id: program.childId ?? program?.child?.id } : { id: program.childId });
        setProgramsModal(true);
    };

    const fetchRepeated = async () => {
        try {
            setLoading(true);
            const [repeated, teacherRes, packageRes, cohortRes] = await Promise.all([
                axios.get(`${baseUrl}/admin/program/repeated`),
                axios.get(`${baseUrl}/admin/teacher/all`),
                axios.get(`${baseUrl}/admin/package/all`, { params: { limit: 10000 } }),
                axios.get(`${baseUrl}/admin/cohort/all`, { params: { limit: 10000 } }),
            ]);

            setRows(Array.isArray(repeated?.data?.data) ? repeated.data.data : []);
            setTeachers(Array.isArray(teacherRes?.data?.data) ? teacherRes.data.data : []);
            // Assign dropdowns only offer active teachers; filters above keep the full list.
            // If this endpoint fails (e.g. API not updated yet), use the full list minus deactivated teachers.
            const assignableRes = await axios.get(`${baseUrl}/admin/teacher/assignable`).catch(() => null);
            setAssignableTeachers(Array.isArray(assignableRes?.data?.data)
                ? assignableRes.data.data
                : (Array.isArray(teacherRes?.data?.data) ? teacherRes?.data?.data : []).filter((teacher) => teacher?.status !== false));

            const pkgData = packageRes?.data?.data;
            setPackages(Array.isArray(pkgData) ? pkgData : (pkgData?.data || []));
            const cohortData = cohortRes?.data?.data;
            setCohorts(Array.isArray(cohortData) ? cohortData : (cohortData?.data || []));

            setSelectedIds([]);
        } catch (error) {
            handleAuthError(error);
            toast.error("Failed to load repeated programs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRepeated();
    }, []);

    const availableIssueCodes = useMemo(() => {
        const set = new Set();
        rows.forEach((p) => (p.issueCodes || []).forEach((c) => set.add(c)));
        return Array.from(set);
    }, [rows]);

    const availableCohorts = useMemo(() => {
        const map = new Map();
        rows.forEach((p) => {
            if (p?.cohort?.id != null) map.set(p.cohort.id, p.cohort.title || `Cohort ${p.cohort.id}`);
        });
        return Array.from(map, ([id, title]) => ({ id, title }));
    }, [rows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((p) => {
            if (issueFilter && !(p.issueCodes || []).includes(issueFilter)) return false;
            if (cohortFilter && String(p?.cohort?.id) !== String(cohortFilter)) return false;
            if (!q) return true;
            const child = `${p?.child?.firstName || ""} ${p?.child?.lastName || ""}`.toLowerCase();
            const parent = `${p?.child?.parent?.firstName || ""} ${p?.child?.parent?.lastName || ""}`.toLowerCase();
            const pkg = `${p?.package?.title || ""}`.toLowerCase();
            return child.includes(q) || parent.includes(q) || pkg.includes(q);
        });
    }, [rows, search, issueFilter, cohortFilter]);

    const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((p) => selectedIds.includes(p.id));

    const toggleSelectAll = () => {
        if (allVisibleSelected) {
            const visibleIds = new Set(filteredRows.map((p) => p.id));
            setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredRows.map((p) => p.id)])));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    // ---- Edit program ----
    const openEdit = (program) => {
        setSelectedProgram(program);
        setProgramMData({});
        setEditModal(true);
    };

    const submitEdit = async () => {
        if (!selectedProgram?.id) {
            toast.warn("Reload this page and try again");
            return;
        }
        if (Object.keys(programMData).length < 1) {
            toast.dark("No changes were made...");
            return;
        }
        try {
            setActionLoadingId(selectedProgram.id);
            await axios.put(`${baseUrl}/admin/program/edit/${selectedProgram.id}`, programMData);
            toast.success("Program updated");
            setEditModal(false);
            await fetchRepeated();
        } catch (error) {
            handleAuthError(error);
            toast.error("Failed to update program");
        } finally {
            setActionLoadingId(null);
        }
    };

    // ---- Assign teacher ----
    const openAssign = (program) => {
        setSelectedProgram(program);
        setSelectedTeacher("");
        setAssignModal(true);
    };

    const assignTeacher = async () => {
        if (!selectedTeacher) {
            toast.warn("Select a teacher first.");
            return;
        }
        try {
            setLoading(true);
            const checkRes = await axios.post(`${baseUrl}/admin/program/check-teacher-schedule-conflicts`, {
                teacherId: Number(selectedTeacher),
                programIds: selectedProgram.id,
            });
            const payload = checkRes.data?.data;
            if (payload?.hasConflict && payload?.conflicts?.length) {
                setScheduleConflicts(payload.conflicts);
                setPendingTeacherPayload({ programIds: selectedProgram.id, teacherId: Number(selectedTeacher) });
                setScheduleConflictModal(true);
                setLoading(false);
                return;
            }
            await axios.post(`${baseUrl}/admin/program/assign/teacher`, {
                programIds: selectedProgram.id,
                teacherId: selectedTeacher,
            });
            toast.success("Teacher assigned successfully.");
            setAssignModal(false);
            await fetchRepeated();
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.data?.conflicts) {
                setScheduleConflicts(error.response.data.data.conflicts);
                setPendingTeacherPayload({ programIds: selectedProgram.id, teacherId: Number(selectedTeacher) });
                setScheduleConflictModal(true);
            } else {
                handleAuthError(error);
                toast.error("Failed to assign teacher.");
            }
        } finally {
            setLoading(false);
        }
    };

    const confirmScheduleConflictAndAssign = async () => {
        if (!pendingTeacherPayload) return;
        try {
            setLoading(true);
            await axios.post(`${baseUrl}/admin/program/assign/teacher`, {
                ...pendingTeacherPayload,
                acknowledgeScheduleConflict: true,
            });
            toast.success("Teacher assigned successfully.");
            setScheduleConflictModal(false);
            setPendingTeacherPayload(null);
            setScheduleConflicts([]);
            setAssignModal(false);
            await fetchRepeated();
        } catch (error) {
            handleAuthError(error);
            toast.error("Failed to assign teacher.");
        } finally {
            setLoading(false);
        }
    };

    // ---- Fix level (single + bulk) ----
    const fixLevels = async (ids, { confirmFirst = true } = {}) => {
        if (!ids || ids.length < 1) {
            toast.error("Select at least one program to fix");
            return;
        }
        if (confirmFirst) {
            const confirmed = await showConfirm({
                title: ids.length > 1 ? `Promote ${ids.length} programs` : "Promote to expected level",
                message:
                    "This moves the program to the expected level (last completed level + 1) by switching it to a matching package for the child's age. Programs without a completed previous program are skipped. Continue?",
                variant: "primary",
            });
            if (!confirmed) return;
        }
        try {
            if (ids.length === 1) setActionLoadingId(ids[0]);
            else setLoading(true);
            const res = await axios.post(`${baseUrl}/admin/program/repeated/fix`, { ids });
            const fixed = res?.data?.data?.fixed ?? 0;
            const skipped = res?.data?.data?.skipped?.length ?? 0;
            toast.success(`Promoted ${fixed} program(s)${skipped ? `, skipped ${skipped}` : ""}`);
            await fetchRepeated();
        } catch (error) {
            handleAuthError(error);
            toast.error("Failed to promote program(s)");
        } finally {
            setActionLoadingId(null);
            setLoading(false);
        }
    };

    // Order the row actions so the most relevant fix for the row's issues comes first (marked "recommended").
    const buildRowActions = (p) => {
        const codes = p.issueCodes || [];
        const hasLevelIssue =
            codes.includes("level_repeat") || codes.includes("level_gap") || codes.includes("level_regression");
        const hasTeacherIssue = codes.includes("teacher_level_mismatch");

        const fixItem = {
            label: "Promote to expected level",
            icon: "mdi-auto-fix",
            onClick: () => fixLevels([p.id]),
            variant: "success",
        };
        const editItem = {
            label: "Edit program",
            icon: "mdi-pencil-outline",
            onClick: () => openEdit(p),
            variant: "danger",
        };
        const assignItem = {
            label: "Assign teacher",
            icon: "mdi-clipboard-account",
            onClick: () => openAssign(p),
            variant: "primary",
        };
        const viewItem = {
            label: "View all programs",
            icon: "mdi-eye-outline",
            onClick: () => openProgramsModal(p),
            variant: "info",
        };

        let order;
        if (hasLevelIssue) order = [fixItem, editItem, assignItem];
        else if (hasTeacherIssue) order = [assignItem, editItem, fixItem];
        else order = [editItem, assignItem, fixItem]; // age / package / missing-package issues

        const withRecommended = order.map((it, i) => (i === 0 ? { ...it, label: `${it.label} (recommended)` } : it));
        return [...withRecommended, viewItem];
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Programs" breadcrumbItem="Manage Repeated" />

                    <Row className="mb-3">
                        <Col md={3}>
                            <Input
                                type="text"
                                placeholder="Search by child, parent or package…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                        <Col md={3} className="mt-2 mt-md-0">
                            <Input
                                type="select"
                                value={issueFilter}
                                onChange={(e) => setIssueFilter(e.target.value)}
                            >
                                <option value="">All issue types</option>
                                {availableIssueCodes.map((code) => (
                                    <option key={code} value={code}>
                                        {ISSUE_LABELS[code] || code}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3} className="mt-2 mt-md-0">
                            <Input
                                type="select"
                                value={cohortFilter}
                                onChange={(e) => setCohortFilter(e.target.value)}
                            >
                                <option value="">All cohorts</option>
                                {availableCohorts.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.title}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3} className="text-md-end mt-2 mt-md-0">
                            <span className="me-3 text-muted">
                                {loading ? "Loading…" : `${filteredRows.length} affected program(s)`}
                            </span>
                            {!readOnly && (
                                <button
                                    className="btn btn-success"
                                    disabled={loading || selectedIds.length < 1}
                                    onClick={() => fixLevels(selectedIds)}
                                >
                                    Fix selected ({selectedIds.length})
                                </button>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <div className="card">
                                <div className="card-body">
                                    <div className="alert alert-warning py-2 small mb-3">
                                        Only <strong>affected</strong> children are listed (newest cohort first). We look
                                        at the child&apos;s whole set of paid programs — <em>active programs count too</em>,
                                        so a normal 1 → 2 → 3 chain is healthy and hidden. Rows where{" "}
                                        <strong>current level = expected level</strong> are also hidden (already on the
                                        right level, e.g. an intentional repeat after the teacher marked not completed).
                                        A child is flagged for: <strong>Repeated level</strong>,{" "}
                                        <strong>Skipped level</strong>, or <strong>Moved backward</strong> when the level
                                        still needs a fix. <strong>Prev. report</strong> shows whether the teacher said
                                        the child completed the previous cohort.
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: 40 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={allVisibleSelected}
                                                            onChange={toggleSelectAll}
                                                        />
                                                    </th>
                                                    <th>Child</th>
                                                    <th>Parent</th>
                                                    <th>Package</th>
                                                    <th>Previous</th>
                                                    <th>Current</th>
                                                    <th>Expected</th>
                                                    <th>Teacher</th>
                                                    <th>Cohort</th>
                                                    <th>Prev. report</th>
                                                    <th>Teacher report</th>
                                                    <th>Issues</th>
                                                    <th className="text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.length < 1 && !loading && (
                                                    <tr>
                                                        <td colSpan={13} className="text-center text-muted py-4">
                                                            No affected programs found. 🎉
                                                        </td>
                                                    </tr>
                                                )}
                                                {filteredRows.map((p) => (
                                                    <tr key={p.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(p.id)}
                                                                onChange={() => toggleSelectOne(p.id)}
                                                            />
                                                        </td>
                                                        <td className="text-capitalize">
                                                            {p?.child?.firstName} {p?.child?.lastName}
                                                        </td>
                                                        <td className="text-capitalize">
                                                            {p?.child?.parent?.firstName} {p?.child?.parent?.lastName}
                                                        </td>
                                                        <td>{p?.package?.title || "—"}</td>
                                                        <td>
                                                            {p.previousLevel != null ? (
                                                                <span
                                                                    className="badge bg-danger"
                                                                    title={p.previousPackageTitle || ""}
                                                                >
                                                                    {p.previousLevel}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted" title="No completed previous program">—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">{p.currentLevel ?? "—"}</span>
                                                        </td>
                                                        <td>
                                                            {p.expectedLevel != null ? (
                                                                <span className="badge bg-success">{p.expectedLevel}</span>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {p?.teacher
                                                                ? `${p.teacher.firstName} ${p.teacher.lastName}`
                                                                : "Not assigned"}
                                                        </td>
                                                        <td>{p?.cohort?.title || "—"}</td>
                                                        <td>
                                                            {p.previousTeacherCohortCompleted === false ? (
                                                                <span
                                                                    className="badge bg-danger"
                                                                    title={
                                                                        p.previousCohortTitle
                                                                            ? `Previous cohort (${p.previousCohortTitle}): teacher marked NOT completed`
                                                                            : "Previous cohort: teacher marked NOT completed"
                                                                    }
                                                                >
                                                                    Held back
                                                                </span>
                                                            ) : p.previousTeacherCohortCompleted === true ? (
                                                                <span
                                                                    className="badge bg-success"
                                                                    title={
                                                                        p.previousCohortTitle
                                                                            ? `Previous cohort (${p.previousCohortTitle}): teacher marked completed`
                                                                            : "Previous cohort: teacher marked completed"
                                                                    }
                                                                >
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className="badge bg-secondary"
                                                                    title={
                                                                        p.previousCohortTitle
                                                                            ? `No teacher report for previous cohort (${p.previousCohortTitle})`
                                                                            : "No previous cohort teacher report"
                                                                    }
                                                                >
                                                                    No report
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {p.teacherCohortCompleted === false ? (
                                                                <span
                                                                    className="badge bg-danger"
                                                                    title="Teacher marked the cohort NOT completed — the teacher held this child back"
                                                                >
                                                                    Held back
                                                                </span>
                                                            ) : p.teacherCohortCompleted === true ? (
                                                                <span
                                                                    className="badge bg-success"
                                                                    title="Teacher marked the cohort completed"
                                                                >
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className="badge bg-secondary"
                                                                    title="No teacher report on file for this program"
                                                                >
                                                                    No report
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td style={{ maxWidth: 260 }}>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {(p.issues || []).map((issue, i) => (
                                                                    <span
                                                                        key={i}
                                                                        className={`badge bg-${severityColor(issue.severity)}`}
                                                                        title={issue.message}
                                                                    >
                                                                        {ISSUE_LABELS[issue.code] || issue.code}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            {!readOnly ? (
                                                                <ActionsDropdown
                                                                    loading={actionLoadingId === p.id}
                                                                    toggleTitle="Program actions"
                                                                    items={buildRowActions(p)}
                                                                />
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Edit modal */}
                    <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)}>
                        <ModalHeader toggle={() => setEditModal(!editModal)}>Modify Program Data</ModalHeader>
                        <ModalBody>
                            <Row>
                                <small className="mb-2 text-danger">
                                    Warning: Check the parent timezone offset before changing day/time.
                                </small>
                                <Col xs={12}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <Label>Cohort</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) =>
                                                        setProgramMData({ ...programMData, cohortId: e.target.value })
                                                    }
                                                >
                                                    <option value="">Select Cohort</option>
                                                    {cohorts.map((d, i) => (
                                                        <option key={i} value={d.id}>
                                                            {d.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <Label>Package</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) =>
                                                        setProgramMData({ ...programMData, packageId: e.target.value })
                                                    }
                                                >
                                                    <option value="">Select Package</option>
                                                    {packages.map((d, i) => (
                                                        <option key={i} value={d.id}>
                                                            {d.title} (lvl {d.level})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <Label>Level</Label>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    defaultValue={selectedProgram?.level}
                                                    onChange={(e) =>
                                                        setProgramMData({ ...programMData, level: Number(e.target.value) })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <Label>Day</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) =>
                                                        setProgramMData({ ...programMData, day: Number(e.target.value) })
                                                    }
                                                >
                                                    <option value="">Select Day</option>
                                                    {days.map((d, i) => (
                                                        <option key={i} value={i}>
                                                            {d}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <Label>Time</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) =>
                                                        setProgramMData({ ...programMData, time: Number(e.target.value) })
                                                    }
                                                >
                                                    <option value="">Select Time</option>
                                                    {TIMES_.map((d, i) => (
                                                        <option key={i} value={i}>
                                                            {d}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <div className="text-end">
                                        <button onClick={submitEdit} className="btn btn-primary save-user">
                                            {actionLoadingId === selectedProgram?.id ? "Saving…" : "Update Program Data"}
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>

                    {/* Schedule conflict modal */}
                    <Modal
                        isOpen={scheduleConflictModal}
                        toggle={() => {
                            setScheduleConflictModal(false);
                            setPendingTeacherPayload(null);
                        }}
                        centered
                    >
                        <ModalHeader
                            toggle={() => {
                                setScheduleConflictModal(false);
                                setPendingTeacherPayload(null);
                            }}
                        >
                            Schedule conflict warning
                        </ModalHeader>
                        <ModalBody>
                            <p className="text-muted">
                                This teacher is already assigned to another program at the same class time while both
                                cohorts still have class access. You can cancel or continue anyway.
                            </p>
                            <ul className="small mb-0">
                                {scheduleConflicts.map((c, idx) => (
                                    <li key={idx} className="mb-2">
                                        <strong>{c.slotLabel || "Slot"}</strong>
                                        {c.message ? <> — {c.message}</> : null}
                                    </li>
                                ))}
                            </ul>
                        </ModalBody>
                        <ModalFooter>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => {
                                    setScheduleConflictModal(false);
                                    setPendingTeacherPayload(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-warning"
                                onClick={confirmScheduleConflictAndAssign}
                                disabled={loading}
                            >
                                {loading ? "…" : "Continue anyway"}
                            </button>
                        </ModalFooter>
                    </Modal>

                    {/* Assign teacher modal */}
                    <Modal isOpen={assignModal} toggle={() => setAssignModal(!assignModal)}>
                        <ModalHeader toggle={() => setAssignModal(!assignModal)}>Assign Teacher</ModalHeader>
                        <ModalBody>
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    assignTeacher();
                                }}
                            >
                                <Row>
                                    <Col xs={12}>
                                        <div className="mb-3">
                                            <Input
                                                name="teacherId"
                                                type="select"
                                                value={selectedTeacher}
                                                onChange={(e) => setSelectedTeacher(e.target.value)}
                                            >
                                                <option value="">Select teacher</option>
                                                {assignableTeachers.map((teacher) => (
                                                    <option key={teacher?.id} value={teacher?.id}>
                                                        {teacher.firstName} {teacher.lastName}
                                                    </option>
                                                ))}
                                            </Input>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div className="text-end">
                                            <button type="submit" className="btn btn-primary save-user" disabled={loading}>
                                                {loading ? "Processing" : "Assign Teacher"}
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </ModalBody>
                    </Modal>

                    {/* View all programs modal */}
                    <ChildProgramsModal
                        isOpen={programsModal}
                        toggle={() => setProgramsModal(!programsModal)}
                        childId={programsChild?.id}
                        childName={programsChild ? `${programsChild.firstName || ""} ${programsChild.lastName || ""}`.trim() : ""}
                    />
                </Container>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default withAuth(ManageRepeated);
